import type { ContextDomain } from './RenderContext';

export interface PhonePair {
  phone?: string | undefined;
  matelsoPhoneLabel?: string | undefined;
}

/** A `{token}` left in place by `substitutePlaceholders` — the field is unset. */
const UNRESOLVED_TOKEN = /\{[^}]+\}/;

/**
 * The 70.07 phone chain for a widget that carries its own `phone` /
 * `matelsoPhoneLabel` fields: widget → Standort → page shell, first tier with
 * a number wins. Mirrors the backend's `PhoneResolutionService.resolve()`.
 *
 * The Standort tier is kept even though `shell` already prefers it: a
 * component that builds its own render context (the CMS preview, the header's
 * contact modal) may have no `domain` entity, and the call CTA must not vanish
 * there.
 *
 * **Both halves always come from the same tier.** Falling back field by field
 * would pair one source's number with another's label and print a number
 * different from the one being dialled, which is what 70.07 calls the hard
 * pair. A tier with a number but no label therefore yields no label, and the
 * caller shows the number itself.
 *
 * An unresolved `{location.*}` token counts as unset: it is a non-empty string,
 * so it would otherwise win the chain and reach an `href` that dials nothing.
 *
 * `shell` is the `domain` context entity, which carries the backend's own
 * location → Domain Setting resolution (`resolvedPhone` /
 * `resolvedMatelsoPhoneLabel`).
 */
export function resolvePhonePair(
  widget: PhonePair,
  location: PhonePair | undefined,
  shell: ContextDomain | undefined,
): PhonePair {
  for (const tier of [widget, location, shell]) {
    const phone = usable(tier?.phone);
    if (phone) {
      return { phone, matelsoPhoneLabel: usable(tier?.matelsoPhoneLabel) };
    }
  }
  return { phone: undefined, matelsoPhoneLabel: undefined };
}

function usable(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && !UNRESOLVED_TOKEN.test(trimmed) ? trimmed : undefined;
}
